import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { generateTypingSequence, type TypingAction } from './demoMockData';

/**
 * Input field wrapper that triggers typing on focus.
 * Used in the demo chat page.
 */
interface DemoInputFieldProps {
  onFocus: () => void;
  typedText: string;
  isTyping: boolean;
  isDisabled: boolean;
  placeholder?: string;
}

export function DemoInputField({
  onFocus,
  typedText,
  isTyping,
  isDisabled,
  placeholder = 'Describe your use case...',
}: DemoInputFieldProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = () => {
    if (!isDisabled) {
      setIsFocused(true);
      onFocus();
    }
  };

  return (
    <div
      className={`
        relative w-full px-4 py-3 pr-12 rounded-lg
        bg-white border-2 transition-all duration-300 shadow-sm
        ${isFocused ? 'border-[#EAA059] ring-2 ring-[#EAA059]/30' : 'border-gray-200'}
        ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-text'}
      `}
      onClick={handleFocus}
      tabIndex={isDisabled ? -1 : 0}
    >
      {typedText ? (
        <span className="text-[#063F6B] text-sm leading-relaxed whitespace-pre-wrap">
          {typedText}
          {isTyping && <TypingCursor />}
        </span>
      ) : (
        <span className="text-gray-400 text-sm">
          {isFocused && !typedText ? (
            <TypingCursor />
          ) : (
            placeholder
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Smooth blinking cursor component
 */
function TypingCursor() {
  return (
    <motion.span
      className="inline-block w-[2px] h-[1.1em] bg-[#063F6B] ml-[1px] align-text-bottom"
      animate={{ opacity: [1, 1, 0, 0] }}
      transition={{ 
        duration: 1, 
        repeat: Infinity,
        times: [0, 0.5, 0.5, 1],
        ease: "linear"
      }}
    />
  );
}

/**
 * Hook to handle natural typing animation with typos and corrections.
 * Returns the current text and typing state.
 */
export function useNaturalTyping(
  isActive: boolean,
  onComplete: () => void
): { typedText: string; isTyping: boolean } {
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const animationRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  useEffect(() => {
    if (!isActive) {
      return;
    }

    // Reset state
    setTypedText('');
    setIsTyping(true);
    animationRef.current = { cancelled: false };

    const sequence = generateTypingSequence();
    let currentIndex = 0;
    let currentText = '';

    const getTypingDelay = (action: TypingAction): number => {
      if (action.duration) return action.duration;

      if (action.action === 'pause') return 350;
      if (action.action === 'delete') return 70 + Math.random() * 30;

      // More natural typing rhythm
      const char = action.char;
      
      // Base speed - slightly slower for more natural feel
      let delay = 55;
      
      // Add variation
      delay += Math.random() * 25;
      
      // Slower after spaces (thinking pause)
      if (char === ' ') delay += 30;
      
      // Slower for punctuation
      if (['.', ',', '!', '?'].includes(char)) delay += 80;
      
      // Slower for apostrophes and quotes
      if (["'", '"'].includes(char)) delay += 40;
      
      // Occasional longer pause (simulating thinking)
      if (Math.random() < 0.05) delay += 100;
      
      return delay;
    };

    const processNext = () => {
      if (animationRef.current.cancelled) return;
      
      if (currentIndex >= sequence.length) {
        // Typing complete
        setIsTyping(false);
        setTimeout(() => {
          if (!animationRef.current.cancelled) {
            onComplete();
          }
        }, 600);
        return;
      }

      const action = sequence[currentIndex];
      const delay = getTypingDelay(action);

      setTimeout(() => {
        if (animationRef.current.cancelled) return;
        
        if (action.action === 'type') {
          currentText += action.char;
        } else if (action.action === 'delete') {
          currentText = currentText.slice(0, -1);
        }
        // 'pause' just waits

        setTypedText(currentText);
        currentIndex++;
        processNext();
      }, delay);
    };

    // Start after initial delay
    setTimeout(processNext, 200);

    return () => {
      animationRef.current.cancelled = true;
    };
  }, [isActive, onComplete]);

  return { typedText, isTyping };
}

// Keep the old export for backwards compatibility but mark as deprecated
export function NaturalTypingEffect() {
  console.warn('NaturalTypingEffect is deprecated. Use useNaturalTyping hook instead.');
  return null;
}
